// Fixed-timestep simulation with a render interpolation alpha.
// Sim runs at a deterministic rate (so seeds reproduce); rendering runs as
// fast as the display allows. Spiral-of-death guard caps catch-up steps.
export type UpdateFn = (dt: number) => void;
export type RenderFn = (alpha: number) => void;

export class GameLoop {
  readonly step: number; // seconds per sim tick
  private acc = 0;
  private last = 0;
  private running = false;
  private raf = 0;

  constructor(
    private update: UpdateFn,
    private render: RenderFn,
    hz = 60,
  ) {
    this.step = 1 / hz;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    this.acc = 0;
    this.raf = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  private frame = (now: number): void => {
    if (!this.running) return;
    this.raf = requestAnimationFrame(this.frame);

    let frameTime = (now - this.last) / 1000;
    this.last = now;
    if (frameTime > 0.25) frameTime = 0.25; // clamp big stalls (tab switch)

    this.acc += frameTime;
    let steps = 0;
    while (this.acc >= this.step && steps < 5) {
      this.update(this.step);
      this.acc -= this.step;
      steps++;
    }
    // If we blew the budget, drop the backlog rather than spiral.
    if (steps >= 5) this.acc = 0;

    this.render(this.acc / this.step);
  };
}
